import type { ReactElement } from "react";
import type { SunViewsReportData } from "../createPropertyReport";
import type { InspectorPresentation } from "./BaseLayersCard";
import { SidetextRow } from "./SidetextRow";

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}

function pct(v: number): string {
  return `${v.toFixed(0)}%`;
}

function ftRound(v: number): string {
  return `${v.toFixed(0)} ft`;
}

function buildMetricRows(data: SunViewsReportData): Array<{ label: string; value: string }> {
  const dateStr = data.dateTime.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
  const sunRow =
    data.bestBuildableSunFraction != null
      ? pct(data.bestBuildableSunFraction * 100)
      : "—";
  const viewshedRow = data.viewshed
    ? `${data.viewshed.visibleAcres.toFixed(1)} ac · ${ftRound(
        data.viewshed.maxRangeFt
      )} max${data.viewshed.fromDefault ? " (default origin)" : ""}`
    : "—";

  return [
    {
      label: "Date",
      value: `${dateStr} · ${fmtTime(data.dateTime)}`
    },
    {
      label: "Daylight",
      value: `${fmtTime(data.sunrise)} → ${fmtTime(data.sunset)} (${data.daylightHours.toFixed(1)} h)`
    },
    {
      label: "Solar noon",
      value: fmtTime(data.solarNoon)
    },
    {
      label: "Buildable sun (Avg)",
      value: sunRow
    },
    {
      label: "Viewshed",
      value: viewshedRow
    }
  ];
}

export interface SunViewsMetricsCardProps {
  report: SunViewsReportData;
  presentation?: InspectorPresentation;
}

export function SunViewsMetricsCard({
  report,
  presentation = "inspector"
}: SunViewsMetricsCardProps): ReactElement {
  const drawerRail = presentation === "drawer";
  const rows = buildMetricRows(report);

  return (
    <div>
      {rows.map((r) => (
        <SidetextRow
          key={r.label}
          label={r.label}
          sideText={r.value}
          multiline={r.value.length > 22}
          drawerRail={drawerRail}
        />
      ))}
    </div>
  );
}
